import { format } from 'date-fns';
import { GetCoachBookingPageQuery } from 'types/generated/client';
import { Lessons } from 'screens/CoachDetails/types';
import { VenueAddress } from 'utils/shared/coachBuilder';
import { dateAsTimezoneFormatted } from 'utils/shared/date/dateAsTimezone';
import { filterSlotsWithBookingLogic } from 'utils/shared/date/mergeDateAndSlot';
import { getTimezone } from 'utils/shared/time/getTimezone';

type CoachAvailability =
  GetCoachBookingPageQuery['userProfiles'][number]['coachAvailability'][number];

export interface timeSlot {
  time: string;
  venue: VenueAddress | null;
}

export function generateTimeSlots(
  startHour: number,
  endHour: number,
  interval: number,
  dateFormat: string,
  timezone = 'America/New_York',
  bookingInMinutes: number = 60,
  venue: VenueAddress | null = null,
): timeSlot[] {
  const timeSlots: timeSlot[] = [];
  const startMinutes = (startHour % 1) * 60;
  const endMinutes = (endHour % 1) * 60;
  const fullStartHour = Math.floor(startHour);
  const fullEndHour = Math.floor(endHour);

  const totalEndTimeMinutes = fullEndHour * 60 + endMinutes;

  for (let hour = fullStartHour; hour <= fullEndHour; hour++) {
    const startMin = hour === fullStartHour ? startMinutes : 0;

    for (let minutes = startMin; minutes < 60; minutes += interval) {
      const slotStartMinutes = hour * 60 + minutes;
      const slotEndMinutes = slotStartMinutes + bookingInMinutes;

      if (slotEndMinutes <= totalEndTimeMinutes) {
        const date = new Date();
        date.setHours(hour, minutes, 0, 0);
        timeSlots.push({
          time: format(dateAsTimezoneFormatted(date, timezone), dateFormat),
          venue,
        });
      }
    }
  }

  return timeSlots;
}

interface GetDayBookableSlotsArgs {
  /** Coach availability windows to consider (already filtered by status/venue selection upstream). */
  availabilities: CoachAvailability[];
  /** The calendar day to build slots for. */
  date: Date;
  /** In-app bookings used to mark slots as booked. */
  lessons: Lessons;
  /** Coach's external (Google) calendar events used to mark slots as booked/overlapping. */
  googleEvents?: any[];
  /** Coach timezone; falls back to the generateTimeSlots default when omitted. */
  timezone?: string | null;
  /** Resolve the venue for a given availability window. Defaults to venue-agnostic (null). */
  getVenue?: (availability: CoachAvailability) => VenueAddress | null;
}

/**
 * Single source of truth for "which slots are bookable on a given day" — the recipe
 * shared by the date-picker dots and the time-picker list. Keeping the slot interval /
 * booking duration here prevents the two consumers from silently drifting apart.
 */
export function getDayBookableSlots({
  availabilities,
  date,
  lessons,
  googleEvents = [],
  timezone,
  getVenue,
}: GetDayBookableSlotsArgs) {
  const dayOfWeek = format(date, 'EEEE').toUpperCase();
  const dayAvailabilities = availabilities.filter(
    (availability) => availability.dayOfWeek === dayOfWeek,
  );

  if (dayAvailabilities.length === 0) return [];

  const daySlots = dayAvailabilities.flatMap((availability) =>
    generateTimeSlots(
      availability.startTimeMinutes / 60,
      availability.endTimeMinutes / 60,
      15,
      'hh:mm a',
      getTimezone(timezone),
      60,
      getVenue ? getVenue(availability) : null,
    ),
  );

  return filterSlotsWithBookingLogic(daySlots, date, lessons, googleEvents);
}
